import { useEffect, useRef, useCallback, useState } from 'react';
import { EstateItem, Classification, Point } from '@types';

export type EstateMapMode = 'aerial' | 'sales';

interface EstateMapProps {
  items: EstateItem[];
  selectedCode: string | null;
  onItemClick: (code: string) => void;
  center: Point;
  className?: string;
  highlightedCodes?: string[];
  showTooltips?: boolean;
  mode?: EstateMapMode;
  alignmentMode?: boolean;
  onEstateTranslate?: (delta: Point) => void;
}

const CLASSIFICATION_COLORS: Record<Classification, string> = {
  lot: '#2CBB65', road: '#2E2E2E', reserve: '#27A675', water: '#21B0F3',
  future: '#9E9E9E', 'approved-context': '#B86D73', 'proposed-context': '#9E7A64',
  excluded: '#646A73', deleted: '#000000',
};

const LOT_STATUS_COLORS: Record<string, string> = {
  available: '#2CBB65', hold: '#2196F3', 'under-offer': '#FF6F00', sold: '#d60014',
};

const SALES_LOT_STATUS_COLORS: Record<string, string> = {
  available: '#82B94B', hold: '#61A9D6', 'under-offer': '#F39A69', sold: '#D67278',
};

const SALES_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#F3F0DF' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#4D5148' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#F3F0DF' }, { weight: 3 }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#C9C7B6' }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#A8C98C' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#D6D5C7' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#A9AB9D' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#55594F' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#9DCBDA' }] },
];

function getCentre(points: Point[]): Point {
  if (points.length === 0) return { lat: 0, lng: 0 };
  const sum = points.reduce(
    (result, point) => ({ lat: result.lat + point.lat, lng: result.lng + point.lng }),
    { lat: 0, lng: 0 }
  );
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
}

export const EstateMap = ({
  items, selectedCode, onItemClick, center, className, highlightedCodes = [],
  showTooltips = true, mode = 'aerial', alignmentMode = false, onEstateTranslate,
}: EstateMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const polygonsRef = useRef<google.maps.Polygon[]>([]);
  const labelsRef = useRef<google.maps.Marker[]>([]);
  const alignmentMarkerRef = useRef<google.maps.Marker | null>(null);
  const hasFitBoundsRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);

  const clearOverlays = useCallback(() => {
    polygonsRef.current.forEach((polygon) => {
      google.maps.event.clearInstanceListeners(polygon);
      polygon.setMap(null);
    });
    labelsRef.current.forEach((label) => label.setMap(null));
    if (alignmentMarkerRef.current) {
      google.maps.event.clearInstanceListeners(alignmentMarkerRef.current);
      alignmentMarkerRef.current.setMap(null);
      alignmentMarkerRef.current = null;
    }
    polygonsRef.current = [];
    labelsRef.current = [];
  }, []);

  useEffect(() => {
    let mounted = true;
    function tryInit() {
      if (!mounted || !mapRef.current) return;
      if (typeof window.google === 'undefined') {
        setTimeout(tryInit, 200);
        return;
      }
      mapInstance.current = new google.maps.Map(mapRef.current, {
        center,
        zoom: 17,
        mapTypeId: mode === 'sales' ? 'roadmap' : 'hybrid',
        styles: mode === 'sales' ? SALES_MAP_STYLES : null,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        clickableIcons: false,
      });
      if (mounted) setMapReady(true);
    }
    tryInit();
    return () => {
      mounted = false;
      clearOverlays();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;
    map.setMapTypeId(mode === 'sales' ? 'roadmap' : 'hybrid');
    map.setOptions({ styles: mode === 'sales' ? SALES_MAP_STYLES : null });
  }, [mode, mapReady]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;
    clearOverlays();

    const bounds = new google.maps.LatLngBounds();
    const highlightedCodeSet = new Set(highlightedCodes);
    const visibleItems = items.filter((item) => item.classification !== 'deleted');

    visibleItems.forEach((item) => {
      const isSelected = item.code === selectedCode;
      const isHighlighted = highlightedCodeSet.has(item.code);
      const isLot = item.classification === 'lot';
      const isApprovedContext = item.classification === 'approved-context';
      const isProposedContext = item.classification === 'proposed-context';
      const isExcluded = item.classification === 'excluded';
      const isRoad = item.classification === 'road';
      const isSales = mode === 'sales';

      const fillColor = isRoad && isSales
        ? '#D1D0C1'
        : item.classification === 'reserve' && isSales
          ? '#78A966'
          : isLot
            ? (isSales ? SALES_LOT_STATUS_COLORS : LOT_STATUS_COLORS)[item.status] || '#82B94B'
            : CLASSIFICATION_COLORS[item.classification];
      const strokeColor = isSelected || isHighlighted
        ? '#FFD24A'
        : isLot
          ? isSales ? '#F9F7EA' : '#FFFFFF'
          : isRoad && isSales
            ? '#9EA092'
            : isApprovedContext ? '#E5A2A6' : fillColor;
      const fillOpacity = isExcluded
        ? 0.06
        : isLot
          ? isSales ? 0.78 : 0.35
          : isRoad
            ? isSales ? 0.9 : isSelected || isHighlighted ? 0.9 : 0.7
            : item.classification === 'reserve' && isSales
              ? 0.78
              : isApprovedContext ? 0.08 : isProposedContext ? 0.34 : 0.25;
      const strokeWeight = isSelected || isHighlighted
        ? 4
        : isLot ? isSales ? 1.5 : 2 : isApprovedContext ? 2 : isExcluded ? 1 : 1.5;
      const zIndex = isSelected ? 10 : isHighlighted ? 9 : isApprovedContext ? 7
        : isRoad ? 6 : isLot ? 5 : isProposedContext ? 3 : isExcluded ? 0 : 2;

      const polygon = new google.maps.Polygon({
        paths: item.points, fillColor, fillOpacity, strokeColor, strokeWeight, zIndex, map,
        clickable: !alignmentMode,
      });

      item.points.forEach((point) => bounds.extend(point));
      polygonsRef.current.push(polygon);
      google.maps.event.addListener(polygon, 'click', () => onItemClick(item.code));

      if (isSales && isLot && item.points.length > 0) {
        labelsRef.current.push(new google.maps.Marker({
          position: getCentre(item.points), map, clickable: false, zIndex: 12,
          icon: { path: google.maps.SymbolPath.CIRCLE, scale: 0 },
          label: { text: item.code, color: '#293027', fontSize: '11px', fontWeight: '700' },
        }));
      }

      if (showTooltips && !isLot && !alignmentMode) {
        const tooltip = new google.maps.InfoWindow();
        google.maps.event.addListener(polygon, 'mouseover', (event: google.maps.MapMouseEvent) => {
          polygon.setOptions({ fillOpacity: isRoad ? 0.9 : 0.5 });
          tooltip.setContent(`<div style="font-size:12px;line-height:1.4;"><strong>${item.classification.toUpperCase()}: ${item.code}</strong><br/>${item.landsize}m² • ${item.frontage}m frontage</div>`);
          tooltip.setPosition(event.latLng!);
          tooltip.open(map);
        });
        google.maps.event.addListener(polygon, 'mouseout', () => {
          polygon.setOptions({ fillOpacity });
          tooltip.close();
        });
      }
    });

    if (!hasFitBoundsRef.current && !bounds.isEmpty()) {
      map.fitBounds(bounds, 40);
      hasFitBoundsRef.current = true;
    }

    if (alignmentMode && visibleItems.length > 0 && onEstateTranslate) {
      const estateCentre = getCentre(visibleItems.flatMap((item) => item.points));
      let dragOrigin = estateCentre;
      let originalPaths: Point[][] = [];
      let originalLabelPositions: Point[] = [];
      const marker = new google.maps.Marker({
        position: estateCentre, map, draggable: true, zIndex: 30,
        title: 'Drag to move the entire estate',
        icon: {
          path: google.maps.SymbolPath.CIRCLE, scale: 13, fillColor: '#F6B93B',
          fillOpacity: 1, strokeColor: '#FFFFFF', strokeWeight: 3,
        },
        label: { text: '✥', color: '#263238', fontSize: '16px', fontWeight: '700' },
      });
      alignmentMarkerRef.current = marker;

      google.maps.event.addListener(marker, 'dragstart', () => {
        const position = marker.getPosition();
        if (position) dragOrigin = { lat: position.lat(), lng: position.lng() };
        originalPaths = visibleItems.map((item) => item.points.map((point) => ({ ...point })));
        originalLabelPositions = labelsRef.current.map((label) => {
          const position = label.getPosition();
          return position ? { lat: position.lat(), lng: position.lng() } : estateCentre;
        });
      });

      google.maps.event.addListener(marker, 'drag', () => {
        const position = marker.getPosition();
        if (!position) return;
        const delta = { lat: position.lat() - dragOrigin.lat, lng: position.lng() - dragOrigin.lng };
        polygonsRef.current.forEach((polygon, index) => {
          polygon.setPath(originalPaths[index].map((point) => ({
            lat: point.lat + delta.lat, lng: point.lng + delta.lng,
          })));
        });
        labelsRef.current.forEach((label, index) => {
          const original = originalLabelPositions[index];
          label.setPosition({ lat: original.lat + delta.lat, lng: original.lng + delta.lng });
        });
      });

      google.maps.event.addListener(marker, 'dragend', () => {
        const position = marker.getPosition();
        if (!position) return;
        const delta = { lat: position.lat() - dragOrigin.lat, lng: position.lng() - dragOrigin.lng };
        if (Math.abs(delta.lat) > 1e-10 || Math.abs(delta.lng) > 1e-10) onEstateTranslate(delta);
      });
    }
  }, [items, selectedCode, highlightedCodes, clearOverlays, onItemClick, mapReady,
    showTooltips, mode, alignmentMode, onEstateTranslate]);

  return <div ref={mapRef} className={className} style={{ width: '100%', minHeight: '400px' }} />;
};
